import { useHead, useRoute } from '#imports';

interface PageData {
  title?: string;
  description?: string;
  ogImage?: {
    asset?: {
      _ref: string;
    };
  };
  author?: string;
  authorImage?: {
    asset?: {
      _ref: string;
    };
  };
  dateModified?: string;
  datePublished?: string;
  _id?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  excludeFromExternalSearch?: boolean;
  enableSchema?: boolean;
}

export function usePageHead(props: PageData) {
  const route = useRoute();
  const baseUrl = process.env.NUXT_BASE_URL || 'https://www.onshape.com';
  const fullUrl = new URL(route.fullPath, baseUrl).toString();
  const resolvedTitle = resolvePageTitle(props.title ?? '');
  const ogImageUrl = props.ogImage ? urlFor(props.ogImage.asset?._ref) : '';
  const authorImageUrl = props.authorImage ? urlFor(props.authorImage?.asset?._ref) : '';
  const defaultTitle = 'Onshape | Product Development Platform';
  const defaultDescription =
    'Onshape Inc. develops the only product design platform that combines 3D CAD, PDM, collaboration and analytics tools in the cloud.';
  const defaultAuthor = 'Onshape, a PTC Business';
  useHead({
    title: resolvedTitle,
    meta: [
      {
        property: 'og:title',
        content: props.title ?? defaultTitle,
      },
      {
        name: 'twitter:title',
        content: props.title ?? defaultTitle,
      },
      {
        name: 'description',
        content: props.description ?? defaultDescription,
      },
      {
        property: 'og:description',
        content: props.description ?? defaultDescription,
      },
      {
        name: 'twitter:description',
        content: props.description ?? defaultDescription,
      },
      { name: 'twitter:card', content: props.twitterCard ?? 'summary_large_image' },
      {
        property: 'og:image',
        content: ogImageUrl,
      },
      { name: 'referrer', content: 'no-referrer-when-downgrade' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: fullUrl },
      {
        name: 'author',
        content: props.author ?? defaultAuthor,
      },
      {
        name: 'asset_id',
        content: props._id ?? '',
      },
      {
        name: 'asset_label',
        content: props.title ?? '',
      },
      {
        name: 'languages',
        content: 'en',
      },
      {
        name: 'robots',
        content:
          props.excludeFromExternalSearch ? 'noindex, nofollow' : (
            'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
          ),
      },
      {
        name: 'facebook-domain-verification',
        content: process.env.FACEBOOK_DOMAIN_VERIFICATION,
      },
    ].filter((meta) => !!meta.content),
    link: [
      {
        rel: 'canonical',
        href: fullUrl.replace(/\/en\/?$/, ''),
      },
    ],
  });

  if (
    props.enableSchema &&
    props.title &&
    props.author &&
    props.dateModified &&
    props.datePublished
  ) {
    const authorUrl = new URL('/en/resource-center/', baseUrl);
    authorUrl.searchParams.set('author', props.author);
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      author: {
        '@type': 'Person',
        name: props.author,
        url: authorUrl.toString(),
      },
      dateModified: new Date(props.dateModified).toISOString(),
      datePublished: new Date(props.datePublished).toISOString(),
      headline: props.title,
      image: [ogImageUrl, authorImageUrl],
      mainEntityOfPage: {
        '@id': fullUrl,
        '@type': 'WebPage',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Onshape',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.onshape.com/global-assets/img/authors/onshape-logo.png',
        },
      },
    };
    useHead({
      script: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(schema),
        },
      ],
    });
  }
}
export function replaceLanguageInUrl(fullUrl: string, newLang: string): string {
  const url = new URL(fullUrl);
  const hasTrailingSlash = url.pathname.endsWith('/');
  const segments = url.pathname.split('/').filter(Boolean);

  // Replace first segment with newLang
  if (segments.length > 0) {
    segments[0] = newLang;
  } else {
    segments.push(newLang);
  }

  // Reconstruct pathname and return full URL
  url.pathname = '/' + segments.join('/');
  url.pathname += hasTrailingSlash ? '/' : ''; // Maintain trailing slash if it was present
  return url.toString();
}
